import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { cn } from '@/lib/utils';

/**
 * Modale de confirmation unifiée - remplace `window.confirm`. Design cohérent
 * avec les autres modales du CRM (overlay `bg-brand/30 backdrop-blur-sm`,
 * card blanche, focus trap, Escape = cancel).
 *
 * Utilisation impérative via `useConfirm()` :
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Supprimer cet événement ?',
 *     message: 'Action irréversible.',
 *     variant: 'danger',
 *     confirmLabel: 'Supprimer',
 *   });
 *   if (!ok) return;
 *
 * L'API promise-based permet de remplacer `window.confirm` au pied levé sans
 * refactorer la structure des fonctions asynchrones qui l'appellent.
 */

export type ConfirmVariant = 'default' | 'warning' | 'danger';

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...options, resolve });
      }),
    [],
  );

  function handleChoice(value: boolean) {
    if (!pending) return;
    pending.resolve(value);
    setPending(null);
  }

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {pending && (
        <ConfirmDialog
          options={pending}
          onConfirm={() => handleChoice(true)}
          onCancel={() => handleChoice(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm() doit être utilisé dans un <ConfirmProvider>');
  return ctx.confirm;
}

// ---------------------------------------------------------------------------
// Composant de rendu - internal
// ---------------------------------------------------------------------------

function ConfirmDialog({
  options,
  onConfirm,
  onCancel,
}: {
  options: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const {
    title,
    message,
    confirmLabel = 'Confirmer',
    cancelLabel = 'Annuler',
    variant = 'default',
  } = options;
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, dialogRef);
  useHotkey('escape', onCancel, { allowInInput: true });
  const [submitting, setSubmitting] = useState(false);

  // Focus automatique sur le bouton principal pour permettre Entrée = confirmer.
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    confirmBtnRef.current?.focus();
  }, []);

  function handleConfirm() {
    setSubmitting(true);
    onConfirm();
  }

  const ICON_TONE: Record<ConfirmVariant, string> = {
    default: 'text-accent bg-accent/10',
    warning: 'text-warning-600 bg-warning-50',
    danger: 'text-danger bg-danger-50',
  };

  const CONFIRM_VARIANT: Record<ConfirmVariant, 'primary' | 'danger'> = {
    default: 'primary',
    warning: 'primary',
    danger: 'danger',
  };

  return (
    <div
      className="animate-fade-in fixed inset-0 z-[60] flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm"
      onClick={onCancel}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        onClick={(e) => e.stopPropagation()}
        className="shadow-modal animate-scale-in w-full max-w-md rounded-lg bg-white p-6"
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
              ICON_TONE[variant],
            )}
          >
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="confirm-title" className="text-base font-semibold text-brand">
              {title}
            </h2>
            {message && <p className="mt-1 text-sm text-text-primary">{message}</p>}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" type="button" onClick={onCancel} disabled={submitting}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmBtnRef}
            variant={CONFIRM_VARIANT[variant]}
            type="button"
            onClick={handleConfirm}
            loading={submitting}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
